/**
 * Per-industry hub configuration for `*-marketing-colorado` pages.
 *
 * Keyed by the route slug. Centralizes:
 *  - hero image import + alt text
 *  - service-grid link overrides (industry-specific spokes when they exist)
 *  - "Resources & guides" slugs (answers + learn pages)
 *  - "Why CWI for [industry]" trust points
 *
 * Add a new industry by adding a new entry here — no route-file edits needed.
 */

import hvacHero from "@/assets/industries/hvac-hero.jpg";
import plumbingHero from "@/assets/industries/plumbing-hero.jpg";
import roofingHero from "@/assets/industries/roofing-hero.jpg";
import electricalHero from "@/assets/industries/electrical-hero.jpg";
import landscapingHero from "@/assets/industries/landscaping-hero.jpg";
import paintingHero from "@/assets/industries/painting-hero.jpg";
import concreteHero from "@/assets/industries/concrete-hero.jpg";
import cleaningHero from "@/assets/industries/cleaning-hero.jpg";
import dentalHero from "@/assets/industries/dental-hero.jpg";
import medicalHero from "@/assets/industries/medical-hero.jpg";
import chiropracticHero from "@/assets/industries/chiropractic-hero.jpg";
import autoDealerHero from "@/assets/industries/auto-dealer-hero.jpg";
import autoRepairHero from "@/assets/industries/auto-repair-hero.jpg";
import garageDoorHero from "@/assets/industries/garage-door-hero.jpg";
import movingHero from "@/assets/industries/moving-hero.jpg";
import pestControlHero from "@/assets/industries/pest-control-hero.jpg";
import treeServiceHero from "@/assets/industries/tree-service-hero.jpg";
import dogTrainerHero from "@/assets/industries/dog-trainer-hero.jpg";
import emailHero from "@/assets/industries/email-hero.jpg";

export type ServiceKey =
  | "seo"
  | "googleMaps"
  | "webDesign"
  | "ppc"
  | "geoAi"
  | "socialMedia";

export type IndustryHubConfig = {
  /** Imported hero image src. */
  heroImage?: string;
  /** SEO alt for the hero image — keyword + Colorado. */
  heroAlt?: string;
  /**
   * Per-service link overrides. If a key is set, the matching service card
   * links here instead of the generic city service page. Use for
   * industry-specific spoke pages like /hvac-seo-colorado.
   */
  serviceLinks?: Partial<Record<ServiceKey, string>>;
  /**
   * Slugs for the "Resources & guides" section. `learn:` prefix resolves to a
   * /learn page; everything else resolves to an /answers slug. 4–6 ideal.
   */
  resourceSlugs?: string[];
  /** Optional 3-point "Why CWI for [industry]" trust strip. */
  trustPoints?: [string, string, string];
};


// Common resource sets reused across similar industries.
const TRADES_BASE: string[] = [
  "how-do-i-rank-in-google-map-pack",
  "how-do-i-get-more-google-reviews",
  "seo-vs-google-ads-which-is-better",
  "local-service-ads-vs-google-ads",
  "learn:local-seo-guide",
  "learn:google-business-profile",
];

const MEDICAL_BASE: string[] = [
  "how-do-i-rank-in-google-map-pack",
  "how-do-i-get-more-google-reviews",
  "how-do-i-show-up-in-google-ai-overviews",
  "how-do-i-get-cited-by-chatgpt",
  "learn:local-seo-guide",
  "learn:google-business-profile",
];

const CONSUMER_SERVICES_BASE: string[] = [
  "how-do-i-rank-in-google-map-pack",
  "how-do-i-get-more-google-reviews",
  "seo-vs-google-ads-which-is-better",
  "do-google-ads-actually-work",
  "learn:local-seo-guide",
  "learn:get-more-reviews",
];

const DEFAULT_TRUST: [string, string, string] = [
  "Owner-led from our Colorado Springs HQ — you talk to the person doing the work",
  "Local-first: we live in Colorado and only take on Colorado clients",
  "No long contracts, no outsourcing, no surprise upsells",
];

export const INDUSTRY_HUB_CONFIG: Record<string, IndustryHubConfig> = {
  "hvac-marketing-colorado": {
    heroImage: hvacHero,
    heroAlt: "HVAC technician servicing a rooftop unit in Colorado — Colorado Web Impressions",
    serviceLinks: {
      seo: "/hvac-seo-colorado",
      googleMaps: "/hvac-google-maps-colorado",
      webDesign: "/hvac-web-design-colorado",
    },
    resourceSlugs: [
      "best-marketing-channel-hvac-colorado",
      "how-to-get-more-hvac-customers-colorado",
      ...TRADES_BASE,
    ],
    trustPoints: DEFAULT_TRUST,
  },
  "plumbing-marketing-colorado": {
    heroImage: plumbingHero,
    heroAlt: "Colorado plumber fixing pipes under a kitchen sink — Colorado Web Impressions",
    serviceLinks: {
      seo: "/plumbing-seo-colorado",
      googleMaps: "/plumbing-google-maps-colorado",
      webDesign: "/plumbing-web-design-colorado",
    },
    resourceSlugs: TRADES_BASE,
    trustPoints: DEFAULT_TRUST,
  },
  "roofing-marketing-colorado": {
    heroImage: roofingHero,
    heroAlt: "Roofing crew installing shingles in Colorado with the Front Range visible — Colorado Web Impressions",
    serviceLinks: {
      seo: "/roofing-seo-colorado",
      googleMaps: "/roofing-google-maps-colorado",
      webDesign: "/roofing-web-design-colorado",
      ppc: "/roofing-ppc-colorado",
    },
    resourceSlugs: [
      "how-roofing-companies-get-more-leads-colorado",
      "why-isnt-my-roofing-company-showing-up-on-google",
      ...TRADES_BASE,
    ],
    trustPoints: DEFAULT_TRUST,
  },
  "electrical-contractor-marketing-colorado": {
    heroImage: electricalHero,
    heroAlt: "Colorado electrician wiring a residential electrical panel — Colorado Web Impressions",
    serviceLinks: {
      seo: "/electrical-seo-colorado",
      googleMaps: "/electrical-google-maps-colorado",
      webDesign: "/electrical-web-design-colorado",
    },
    resourceSlugs: [
      "how-do-electricians-get-more-jobs-colorado",
      ...TRADES_BASE,
    ],
    trustPoints: DEFAULT_TRUST,
  },
  "landscaping-marketing-colorado": {
    heroImage: landscapingHero,
    heroAlt: "Landscaping crew installing flagstone patio at a Colorado home — Colorado Web Impressions",
    serviceLinks: {
      seo: "/landscaping-seo-colorado",
      webDesign: "/landscaping-web-design-colorado",
    },
    resourceSlugs: [
      "how-do-landscapers-get-more-jobs-colorado",
      ...TRADES_BASE,
    ],
    trustPoints: DEFAULT_TRUST,
  },
  "painting-marketing-colorado": {
    heroImage: paintingHero,
    heroAlt: "Painter on a ladder painting trim on a Colorado craftsman home — Colorado Web Impressions",
    serviceLinks: {
      webDesign: "/painting-web-design-colorado",
    },
    resourceSlugs: [
      "how-do-painters-get-more-jobs-from-google",
      ...TRADES_BASE,
    ],
    trustPoints: DEFAULT_TRUST,
  },
  "concrete-marketing-colorado": {
    heroImage: concreteHero,
    heroAlt: "Concrete contractor finishing a fresh driveway pour in Colorado — Colorado Web Impressions",
    serviceLinks: {
      webDesign: "/concrete-web-design-colorado",
    },
    resourceSlugs: [
      "how-to-get-more-concrete-jobs-colorado",
      ...TRADES_BASE,
    ],
    trustPoints: DEFAULT_TRUST,
  },
  "cleaning-marketing-colorado": {
    heroImage: cleaningHero,
    heroAlt: "Professional cleaner in a bright modern Colorado kitchen — Colorado Web Impressions",
    serviceLinks: {
      webDesign: "/cleaning-web-design-colorado",
    },
    resourceSlugs: CONSUMER_SERVICES_BASE,
    trustPoints: DEFAULT_TRUST,
  },
  "dental-marketing-colorado": {
    heroImage: dentalHero,
    heroAlt: "Modern Colorado dental practice operatory — Colorado Web Impressions",
    serviceLinks: {
      seo: "/dental-seo-colorado",
      googleMaps: "/dental-google-maps-colorado",
      webDesign: "/dental-web-design-colorado",
    },
    resourceSlugs: MEDICAL_BASE,
    trustPoints: DEFAULT_TRUST,
  },
  "medical-marketing-colorado": {
    heroImage: medicalHero,
    heroAlt: "Colorado physician consulting with a patient in a modern clinic — Colorado Web Impressions",
    serviceLinks: {
      seo: "/medical-seo-colorado",
      webDesign: "/medical-web-design-colorado",
    },
    resourceSlugs: MEDICAL_BASE,
    trustPoints: DEFAULT_TRUST,
  },
  "chiropractic-marketing-colorado": {
    heroImage: chiropracticHero,
    heroAlt: "Chiropractor adjusting a patient in a Colorado clinic — Colorado Web Impressions",
    serviceLinks: {
      webDesign: "/chiropractic-web-design-colorado",
    },
    resourceSlugs: MEDICAL_BASE,
    trustPoints: DEFAULT_TRUST,
  },
  "auto-dealer-marketing-colorado": {
    heroImage: autoDealerHero,
    heroAlt: "Modern Colorado car dealership showroom with new vehicles — Colorado Web Impressions",
    serviceLinks: {
      webDesign: "/auto-dealer-web-design-colorado",
    },
    resourceSlugs: CONSUMER_SERVICES_BASE,
    trustPoints: DEFAULT_TRUST,
  },
  "auto-repair-marketing-colorado": {
    heroImage: autoRepairHero,
    heroAlt: "Auto mechanic working on a vehicle in a Colorado repair shop — Colorado Web Impressions",
    serviceLinks: {
      webDesign: "/auto-repair-web-design-colorado",
    },
    resourceSlugs: CONSUMER_SERVICES_BASE,
    trustPoints: DEFAULT_TRUST,
  },
  "garage-door-marketing-colorado": {
    heroImage: garageDoorHero,
    heroAlt: "Garage door technician installing a new door at a Colorado home — Colorado Web Impressions",
    serviceLinks: {
      webDesign: "/garage-door-web-design-colorado",
    },
    resourceSlugs: [
      "why-isnt-my-garage-door-company-showing-up-on-google",
      ...TRADES_BASE,
    ],
    trustPoints: DEFAULT_TRUST,
  },
  "moving-company-marketing-colorado": {
    heroImage: movingHero,
    heroAlt: "Professional movers loading a truck at a Colorado home — Colorado Web Impressions",
    serviceLinks: {
      webDesign: "/moving-company-web-design-colorado",
    },
    resourceSlugs: [
      "how-to-get-more-moving-leads-colorado-springs",
      ...CONSUMER_SERVICES_BASE,
    ],
    trustPoints: DEFAULT_TRUST,
  },
  "pest-control-marketing-colorado": {
    heroImage: pestControlHero,
    heroAlt: "Pest control technician treating a Colorado home — Colorado Web Impressions",
    serviceLinks: {
      webDesign: "/pest-control-web-design-colorado",
    },
    resourceSlugs: CONSUMER_SERVICES_BASE,
    trustPoints: DEFAULT_TRUST,
  },
  "tree-service-marketing-colorado": {
    heroImage: treeServiceHero,
    heroAlt: "Arborist pruning a large tree from a bucket truck in Colorado — Colorado Web Impressions",
    serviceLinks: {
      webDesign: "/tree-service-web-design-colorado",
    },
    resourceSlugs: TRADES_BASE,
    trustPoints: DEFAULT_TRUST,
  },
  "dog-trainer-marketing-colorado": {
    heroImage: dogTrainerHero,
    heroAlt: "Dog trainer working with a Labrador in a Colorado mountain setting — Colorado Web Impressions",
    serviceLinks: {
      webDesign: "/dog-trainer-web-design-colorado",
    },
    resourceSlugs: CONSUMER_SERVICES_BASE,
    trustPoints: DEFAULT_TRUST,
  },
  "email-marketing-colorado": {
    heroImage: emailHero,
    heroAlt: "Email marketing campaign dashboard on a laptop in a Colorado office — Colorado Web Impressions",
    resourceSlugs: [
      "do-google-ads-actually-work",
      "seo-vs-google-ads-which-is-better",
      "how-do-i-get-more-google-reviews",
      "learn:website-conversion",
      "learn:marketing-budget",
    ],
    trustPoints: DEFAULT_TRUST,
  },
  "accountant-marketing-colorado": {
    serviceLinks: {
      seo: "/accountant-seo-colorado",
      webDesign: "/accountant-web-design-colorado",
    },
    trustPoints: DEFAULT_TRUST,
  },
  "snow-removal-marketing-colorado": {
    serviceLinks: {
      seo: "/snow-removal-seo-colorado",
      webDesign: "/snow-removal-web-design-colorado",
    },
    trustPoints: DEFAULT_TRUST,
  },
  "fireplace-chimney-marketing-colorado": {
    serviceLinks: {
      seo: "/chimney-sweep-seo-colorado",
      webDesign: "/chimney-sweep-web-design-colorado",
    },
    trustPoints: DEFAULT_TRUST,
  },
  "insurance-agency-marketing-colorado": {
    serviceLinks: {
      seo: "/insurance-agent-seo-colorado",
      webDesign: "/insurance-agent-web-design-colorado",
    },
    trustPoints: DEFAULT_TRUST,
  },
  "real-estate-marketing-colorado": {
    serviceLinks: {
      seo: "/real-estate-agent-seo-colorado",
      webDesign: "/real-estate-agent-web-design-colorado",
    },
    trustPoints: DEFAULT_TRUST,
  },
  "tutor-marketing-colorado": {
    serviceLinks: {
      seo: "/tutor-seo-colorado",
      webDesign: "/tutor-web-design-colorado",
    },
    trustPoints: DEFAULT_TRUST,
  },
  "photographer-marketing-colorado": {
    serviceLinks: {
      seo: "/photographer-seo-colorado",
      webDesign: "/photographer-web-design-colorado",
    },
    trustPoints: DEFAULT_TRUST,
  },
  "gym-marketing-colorado": {
    serviceLinks: {
      seo: "/gym-seo-colorado",
      webDesign: "/gym-web-design-colorado",
    },
    trustPoints: DEFAULT_TRUST,
  },
  "salon-spa-marketing-colorado": {
    serviceLinks: {
      seo: "/salon-seo-colorado",
      webDesign: "/salon-web-design-colorado",
    },
    trustPoints: DEFAULT_TRUST,
  },
  "boutique-retail-marketing-colorado": {
    serviceLinks: {
      seo: "/ecommerce-seo-colorado",
      webDesign: "/web-design",
    },
    trustPoints: DEFAULT_TRUST,
  },
};


export function getIndustryHubConfig(slug: string): IndustryHubConfig | null {
  return INDUSTRY_HUB_CONFIG[slug] ?? null;
}
